/**
 * Figgy Draft Builder — TypeScript types
 *
 * These types describe the figgy_drafts table and the shapes
 * used across the admin draft pages and public preview.
 */

// ── Draft status lifecycle ──────────────────────────────────────────

export type DraftStatus = 'draft' | 'preview_ready' | 'sent' | 'claimed' | 'archived';

export type ImportStatus = 'pending' | 'imported' | 'failed' | 'reviewed';

// ── Structured JSONB shapes ─────────────────────────────────────────

export interface DraftService {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration: number; // minutes
}

export interface DraftCustomLink {
  id: string;
  title: string;
  url: string;
  meta?: string;
}

// ── Full draft row (admin use) ──────────────────────────────────────

export interface FiggyDraft {
  id: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;

  status: DraftStatus;
  preview_token: string;

  // Identity
  instagram_handle: string | null;
  instagram_url: string | null;
  business_name: string | null;
  provider_name: string | null;
  category: string | null;
  city: string | null;
  website_url: string | null;
  booking_url: string | null;
  phone: string | null;
  profile_image_url: string | null;

  // Branding
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;

  // Copy
  bio: string | null;
  hero_headline: string | null;
  hero_subheadline: string | null;
  cta_label: string | null;

  // Structured data
  services: DraftService[];
  custom_links: DraftCustomLink[];

  // Import provenance
  source_url: string | null;
  source_platform: string | null;
  source_data: Record<string, unknown> | null;
  import_status: ImportStatus | null;
  import_confidence: number | null;
  imported_at: string | null;
  extracted_images: string[];
  extraction_notes: string | null;

  // Internal
  notes: string | null;

  // Claim/conversion
  claimed_by_user_id: string | null;
  claimed_business_profile_id: string | null;
}

// ── Input for creating a new draft ──────────────────────────────────

export interface CreateDraftInput {
  instagram_handle?: string;
  business_name?: string;
  provider_name?: string;
  category?: string;
  city?: string;
  website_url?: string;
  booking_url?: string;
  phone?: string;
  notes?: string;
}

/**
 * Operator-editable fields for the Draft Builder editor update path.
 * Lifecycle-owned fields (claim metadata, import provenance, ids, tokens)
 * are intentionally excluded.
 */
export type DraftUpdateInput = Partial<{
  status: DraftStatus;
  instagram_handle: string | null;
  instagram_url: string | null;
  business_name: string | null;
  provider_name: string | null;
  category: string | null;
  city: string | null;
  website_url: string | null;
  booking_url: string | null;
  phone: string | null;
  profile_image_url: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
  bio: string | null;
  hero_headline: string | null;
  hero_subheadline: string | null;
  cta_label: string | null;
  services: DraftService[];
  custom_links: DraftCustomLink[];
  notes: string | null;
}>;

export const DRAFT_UPDATE_KEYS = [
  'status',
  'instagram_handle',
  'instagram_url',
  'business_name',
  'provider_name',
  'category',
  'city',
  'website_url',
  'booking_url',
  'phone',
  'profile_image_url',
  'brand_primary_color',
  'brand_secondary_color',
  'brand_accent_color',
  'bio',
  'hero_headline',
  'hero_subheadline',
  'cta_label',
  'services',
  'custom_links',
  'notes',
] as const satisfies ReadonlyArray<keyof DraftUpdateInput>;

export function isDraftPreviewShareable(status: DraftStatus | null | undefined): boolean {
  return status === 'preview_ready' || status === 'sent';
}

/**
 * Draft editor share-gate decision.
 *
 * Copy Link / Open Preview key off the DATABASE (persisted) status only.
 * Changing the local status select to preview_ready does not enable share
 * until Save succeeds and persistedStatus updates.
 *
 * Inverse: if persisted is already shareable and local select changes away
 * from preview_ready without saving, buttons stay enabled — they still
 * reflect what the public preview endpoint will accept from the DB.
 */
export function getDraftEditorPreviewShareGate(
  persistedStatus: DraftStatus | null | undefined,
  localStatus: DraftStatus | null | undefined,
): {
  previewButtonsEnabled: boolean;
  needsSaveForPreview: boolean;
} {
  const previewButtonsEnabled = isDraftPreviewShareable(persistedStatus);
  const localStatusShareable = isDraftPreviewShareable(localStatus);
  return {
    previewButtonsEnabled,
    needsSaveForPreview: localStatusShareable && !previewButtonsEnabled,
  };
}

// ── Public preview view (returned by get_draft_preview RPC) ─────────

export interface DraftPublicView {
  id: string;
  business_name: string | null;
  provider_name: string | null;
  category: string | null;
  city: string | null;
  bio: string | null;
  hero_headline: string | null;
  hero_subheadline: string | null;
  cta_label: string | null;
  profile_image_url: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
  instagram_url: string | null;
  booking_url: string | null;
  website_url: string | null;
  services: DraftService[];
  custom_links: DraftCustomLink[];
  extracted_images: string[];
}
